import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Check, Plus, X } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Selo } from '../../componentes/base';
import { Vazio } from '../../componentes/estados';
import { AreaSensivel, Formulario, Rotulo, Selecao, type DefCampo } from '../../componentes/formulario';
import { Confirmar, Drawer, Modal } from '../../componentes/sobreposicao';
import { Tabela, type Coluna } from '../../componentes/tabela';
import { useToast } from '../../componentes/toast';
import { getTodos, mensagemDeErro, post } from '../../nucleo/api';
import { data, dataHora, nomePessoa, textoLivre } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';
import { COR_CATEGORIA, COR_ESTADO, ESTADOS, invalidarAusencias, type Estado, type Pedido, type Tipo } from './tipos';

/** Pedidos (todos, com filtro de situação) e Aprovações (os em aberto, com a decisão). */
function ListaPedidos({ aprovacoes }: { aprovacoes: boolean }) {
  const { t } = useTranslation('ausencias');
  const s = useSessao();
  const [estado, setEstado] = useState<Estado | ''>('');
  const [aberto, setAberto] = useState<Pedido | 'novo' | null>(null);

  const colunas: Coluna<Pedido>[] = [
    { chave: 'funcionario_nome', rotulo: t('campos.funcionario'), principal: true, render: (p) => <span className="font-medium">{nomePessoa(p.funcionario_nome) || '-'}</span> },
    { chave: 'tipo_nome', rotulo: t('campos.tipo'), render: (p) => <Selo cor={COR_CATEGORIA[p.categoria]}>{p.tipo_nome}</Selo> },
    { chave: 'inicio', rotulo: t('campos.inicio'), render: (p) => data(p.inicio) },
    { chave: 'fim', rotulo: t('campos.fim'), render: (p) => data(p.fim) },
    { chave: 'dias', rotulo: t('campos.dias'), alinhar: 'direita', mono: true },
    { chave: 'estado', rotulo: t('campos.estado'), render: (p) => <Selo cor={COR_ESTADO[p.estado]}>{t(`estado.${p.estado}`)}</Selo> },
    { chave: 'observacao', rotulo: t('campos.observacao'), oculta: true, render: (p) => textoLivre(p.observacao, p.funcionario_nome) },
  ];
  const params = useMemo(() => (aprovacoes ? { estado: 'pedido' } : estado ? { estado } : {}), [aprovacoes, estado]);
  const props = {
    caminho: '/ausencias/pedidos',
    params,
    chaveConsulta: ['ausencias', 'pedidos', aprovacoes ? 'aprovacoes' : 'todos', estado],
    colunas,
    aoClicar: (p: Pedido) => setAberto(p),
    buscaNaUrl: true,
    filtros: aprovacoes ? undefined : { estado },
    aoAplicarFiltros: aprovacoes ? undefined : (f: Record<string, unknown>) => setEstado(ESTADOS.includes(f.estado as Estado) ? (f.estado as Estado) : ''),
    filtrosExtra: aprovacoes ? undefined : (
      <Selecao
        aria-label={t('campos.estado')}
        value={estado}
        onChange={(e) => setEstado(e.target.value as Estado | '')}
        opcoes={[{ valor: '', rotulo: t('estado.todos') }, ...ESTADOS.map((x) => ({ valor: x, rotulo: t(`estado.${x}`) }))]}
      />
    ),
    vazio: <Vazio texto={aprovacoes ? t('vazio.aprovacoes') : t('vazio.pedidos')} />,
  };

  return (
    <>
      {!aprovacoes && s.pode('ausencias.criar') && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<Plus className="size-4" />} onClick={() => setAberto('novo')}>
            {t('acoes.novoPedido')}
          </Botao>
        </AcaoPrimaria>
      )}
      {aprovacoes ? <Tabela<Pedido> tela="ausencias.aprovacoes" {...props} /> : <Tabela<Pedido> tela="ausencias.pedidos" {...props} />}
      {aberto === 'novo' && <NovoPedido aoFechar={() => setAberto(null)} />}
      {aberto && aberto !== 'novo' && <FichaPedido pedido={aberto} aoFechar={() => setAberto(null)} />}
    </>
  );
}

export const Pedidos = () => <ListaPedidos aprovacoes={false} />;
export const Aprovacoes = () => <ListaPedidos aprovacoes />;

function NovoPedido({ aoFechar }: { aoFechar: () => void }) {
  const { t } = useTranslation('ausencias');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const qc = useQueryClient();
  const toast = useToast();
  const tipos = useQuery({ queryKey: ['ausencias', 'tipos', 'ativos'], queryFn: () => getTodos<Tipo>('/ausencias/tipos', { ativo: 'true' }) });
  const outro = s.pode('ausencias.editar');
  const campos: DefCampo[] = [
    ...(outro
      ? [{ nome: 'funcionario_id', rotulo: t('campos.funcionario'), tipo: 'busca', ajuda: t('campos.funcionarioAjuda'), busca: { caminho: '/pessoas/funcionarios', rotulo: (i: Record<string, unknown>) => nomePessoa(String(i.nome ?? '')) } } as DefCampo]
      : []),
    { nome: 'tipo_id', rotulo: t('campos.tipo'), tipo: 'selecao', obrigatorio: true, opcoes: (tipos.data?.itens ?? []).map((x) => ({ valor: x.id, rotulo: x.nome })) },
    { nome: 'inicio', rotulo: t('campos.inicio'), tipo: 'data', obrigatorio: true, largura: 'meia' },
    { nome: 'fim', rotulo: t('campos.fim'), tipo: 'data', obrigatorio: true, largura: 'meia' },
    { nome: 'observacao', rotulo: t('campos.observacao'), tipo: 'area' },
  ];
  return (
    <Drawer
      aberto
      aoFechar={aoFechar}
      titulo={t('acoes.novoPedido')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-pedido-ausencia">
            {tn('comum.salvar')}
          </Botao>
        </>
      }
    >
      {!outro && <p className="mb-3 text-sm text-tinta-3">{t('aviso.semPessoas')}</p>}
      <Formulario
        key={tipos.data ? 'pronto' : 'carregando'}
        id="form-pedido-ausencia"
        campos={campos}
        aoEnviar={async (v) => {
          await post('/ausencias/pedidos', { funcionario_id: v.funcionario_id || null, tipo_id: v.tipo_id, inicio: v.inicio, fim: v.fim, observacao: v.observacao || null });
          await invalidarAusencias(qc);
          toast.sucesso(tn('comum.salvo'));
          aoFechar();
        }}
      />
    </Drawer>
  );
}

export function FichaPedido({ pedido, aoFechar }: { pedido: Pedido; aoFechar: () => void }) {
  const { t } = useTranslation('ausencias');
  const { t: tn } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const [recusando, setRecusando] = useState(false);
  const [motivo, setMotivo] = useState('');
  const [cancelando, setCancelando] = useState(false);
  const acao = useMutation({
    mutationFn: ({ qual, corpo }: { qual: 'aprovar' | 'recusar' | 'cancelar'; corpo?: unknown }) => post(`/ausencias/pedidos/${pedido.id}/${qual}`, corpo),
    onSuccess: async () => {
      await invalidarAusencias(qc);
      toast.sucesso(tn('comum.salvo'));
      aoFechar();
    },
    onError: (e) => {
      setCancelando(false);
      toast.erro(mensagemDeErro(e));
    },
  });
  const linha = (rotulo: string, valor: React.ReactNode) => (
    <div className="flex flex-wrap justify-between gap-2 border-b border-borda py-2 text-sm last:border-0">
      <dt className="text-tinta-3">{rotulo}</dt>
      <dd className="min-w-0 text-right text-tinta-1">{valor}</dd>
    </div>
  );
  return (
    <>
      <Drawer
        aberto={!recusando}
        aoFechar={aoFechar}
        titulo={`${nomePessoa(pedido.funcionario_nome)} · ${pedido.tipo_nome}`}
        rodape={
          <>
            {pedido.pode_cancelar && (
              <Botao variante="perigo" onClick={() => setCancelando(true)}>
                {t('acoes.cancelarPedido')}
              </Botao>
            )}
            {pedido.pode_decidir && (
              <>
                <Botao icone={<X className="size-4" />} onClick={() => setRecusando(true)}>
                  {t('acoes.recusar')}
                </Botao>
                <Botao variante="primario" icone={<Check className="size-4" />} carregando={acao.isPending} onClick={() => acao.mutate({ qual: 'aprovar' })}>
                  {t('acoes.aprovar')}
                </Botao>
              </>
            )}
          </>
        }
      >
        <dl>
          {linha(t('campos.estado'), <Selo cor={COR_ESTADO[pedido.estado]}>{t(`estado.${pedido.estado}`)}</Selo>)}
          {linha(t('campos.tipo'), `${pedido.tipo_nome} (${pedido.remunerada ? t('remunerada') : t('naoRemunerada')})`)}
          {linha(t('campos.inicio'), t('periodo', { de: data(pedido.inicio), ate: data(pedido.fim) }))}
          {linha(t('campos.dias'), t('diasN', { count: pedido.dias }))}
          {pedido.observacao && linha(t('campos.observacao'), textoLivre(pedido.observacao, pedido.funcionario_nome))}
          {pedido.decidido_em && linha(t('campos.decididoPor'), `${nomePessoa(pedido.decidido_por_nome) || '-'} · ${dataHora(pedido.decidido_em)}`)}
          {pedido.motivo_decisao && linha(t('campos.motivo'), textoLivre(pedido.motivo_decisao, pedido.funcionario_nome))}
        </dl>
      </Drawer>
      <Modal
        aberto={recusando}
        aoFechar={() => setRecusando(false)}
        titulo={t('recusar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setRecusando(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" carregando={acao.isPending} aria-disabled={!motivo.trim()} onClick={() => motivo.trim() && acao.mutate({ qual: 'recusar', corpo: { motivo } })}>
              {t('acoes.recusar')}
            </Botao>
          </>
        }
      >
        <Rotulo id="ausencia-motivo" rotulo={t('campos.motivo')} obrigatorio ajuda={t('recusar.ajuda')}>
          <AreaSensivel
            id="ausencia-motivo"
            rows={3}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
        </Rotulo>
      </Modal>
      <Confirmar aberto={cancelando} aoFechar={() => setCancelando(false)} aoConfirmar={() => acao.mutate({ qual: 'cancelar' })} titulo={t('confirmar.cancelar')} texto={t('confirmar.cancelarTexto')} perigoso carregando={acao.isPending} />
    </>
  );
}
